import {Component, OnInit} from '@angular/core';
import {TracciatiService} from '../../../_services/tracciato.service';
import {TracciatoField} from '../../../_models/TracciatoField';
import {ModalController, SelectCustomEvent} from '@ionic/angular';
import {SaveMessage} from '../../../_helpers/save-message';
import {TracciatoFieldTypesEnum} from '../../../_helpers/TracciatoFieldTypes';
import {TRACCIATO_FIELDS, TracciatoFieldsEnum} from '../../../_helpers/TracciatoFields';
import {TracciatoFieldKeyTypes} from '../../../_helpers/TracciatoFieldKeyTypes';
import {CampiUtenteService} from '../../../_services/campi-utente.service';
import {OrganizzazioniService} from '../../../_services/organizzazioni.service';
import { CampoUtenteDef } from '../../../_models/CampoUtenteDef';
import { EntitaCampoUtente } from '../../../_models/generated/entita-campo-utente';

const emptyField: TracciatoField = {
  tracciatoId: 0,
  fieldId: 0,
  fieldType: TracciatoFieldTypesEnum.standard,
  columnName: '',
  fieldName: '',
  sortIndex: 1,
  fieldLength: 0,
  keyType: TracciatoFieldKeyTypes.none,
  // Esplicito e non omesso: senza, un campo nuovo partirebbe `undefined` e viaggerebbe cosi' nel
  // salvataggio, dove la colonna e' `bool NOT NULL` (feature 137, FR-043).
  skipRowIfEmpty: false
};

/**
 * Tipi campo che mostrano una data card della modale. Sostituiscono le catene di `*ngIf` numeriche
 * del template: con dieci sorgenti, l'undicesima si aggiunge qui e non in sei punti dell'HTML.
 * ⚠️ Il tipo `operatore` (15) si comporta come `ncCodice` (14), `operatoreCampoUtente` (16) come i
 * due tipi campo utente esistenti (12/13). Feature 138: `operatoreBadge` (17) si comporta come
 * `operatore` (15) e `operatoreBadgeCampoUtente` (18) come `operatoreCampoUtente` (16), ⛔ con
 * l'unica eccezione di `SKIP_ROW_IF_EMPTY_FIELD_TYPES`.
 */
const KEY_TYPE_CARD_FIELD_TYPES = new Set<TracciatoFieldTypesEnum>([
  TracciatoFieldTypesEnum.standard, TracciatoFieldTypesEnum.fixed, TracciatoFieldTypesEnum.special,
  TracciatoFieldTypesEnum.movement, TracciatoFieldTypesEnum.registry, TracciatoFieldTypesEnum.ordineVendita,
  TracciatoFieldTypesEnum.ordineVenditaCampoUtente, TracciatoFieldTypesEnum.anagraficaCampoUtente,
  TracciatoFieldTypesEnum.ncCodice, TracciatoFieldTypesEnum.operatore,
  TracciatoFieldTypesEnum.operatoreCampoUtente, TracciatoFieldTypesEnum.operatoreBadge,
  TracciatoFieldTypesEnum.operatoreBadgeCampoUtente
]);

const FIELD_NAME_CARD_FIELD_TYPES = new Set<TracciatoFieldTypesEnum>([
  TracciatoFieldTypesEnum.standard, TracciatoFieldTypesEnum.fixed, TracciatoFieldTypesEnum.special,
  TracciatoFieldTypesEnum.quality, TracciatoFieldTypesEnum.box, TracciatoFieldTypesEnum.causale,
  TracciatoFieldTypesEnum.movement, TracciatoFieldTypesEnum.registry, TracciatoFieldTypesEnum.ordineVendita,
  TracciatoFieldTypesEnum.ncCodice, TracciatoFieldTypesEnum.operatore,
  TracciatoFieldTypesEnum.operatoreBadge
]);

const FORMAT_CARD_FIELD_TYPES = new Set<TracciatoFieldTypesEnum>([
  TracciatoFieldTypesEnum.standard, TracciatoFieldTypesEnum.fixed, TracciatoFieldTypesEnum.special,
  TracciatoFieldTypesEnum.external, TracciatoFieldTypesEnum.quality, TracciatoFieldTypesEnum.movement,
  TracciatoFieldTypesEnum.registry, TracciatoFieldTypesEnum.ordineVendita,
  TracciatoFieldTypesEnum.ordineVenditaCampoUtente, TracciatoFieldTypesEnum.anagraficaCampoUtente,
  TracciatoFieldTypesEnum.ncCodice, TracciatoFieldTypesEnum.operatore,
  TracciatoFieldTypesEnum.operatoreCampoUtente, TracciatoFieldTypesEnum.operatoreBadge,
  TracciatoFieldTypesEnum.operatoreBadgeCampoUtente
]);

/** Tipi campo il cui "Formato" non e' una regex ma il tipo in cui convertire il valore. */
const TIPO_CAMPO_FORMAT_FIELD_TYPES = new Set<TracciatoFieldTypesEnum>([
  TracciatoFieldTypesEnum.movement, TracciatoFieldTypesEnum.registry, TracciatoFieldTypesEnum.ordineVendita,
  TracciatoFieldTypesEnum.ordineVenditaCampoUtente, TracciatoFieldTypesEnum.anagraficaCampoUtente,
  TracciatoFieldTypesEnum.operatoreCampoUtente, TracciatoFieldTypesEnum.operatoreBadgeCampoUtente
]);

/**
 * Tipi campo su cui la casella «salta la riga se il campo e' vuoto» ha effetto (feature 137,
 * FR-043). ⛔ La colonna esiste su tutte le sorgenti, ma in questa feature e' onorata dal solo
 * canale Operatori: mostrarla altrove sarebbe una casella che non fa nulla.
 */
const SKIP_ROW_IF_EMPTY_FIELD_TYPES = new Set<TracciatoFieldTypesEnum>([
  TracciatoFieldTypesEnum.operatore, TracciatoFieldTypesEnum.operatoreCampoUtente
  // ⛔ I tipi 17/18 del canale badge NON sono qui (feature 138, FR-031), e il motivo e' di
  //    sicurezza, non di costo: quel filtro salta il tracciato quando non lascia passare nessuna
  //    riga, e su un canale di controllo accessi un tracciato saltato lascia valide le tessere
  //    revocate. Una casella visibile dove non fa nulla e' peggio della sua assenza.
]);

/**
 * Entità di campo utente servita da ciascun tipo campo dinamico. Mappa esplicita e non ternario:
 * con tre entità (e una quarta in arrivo) un `? :` annidato sbaglia in silenzio — l'elenco
 * caricato sarebbe quello di un'altra entità, senza errore visibile.
 */
const CAMPO_UTENTE_ENTITA_BY_FIELD_TYPE = new Map<TracciatoFieldTypesEnum, EntitaCampoUtente>([
  [TracciatoFieldTypesEnum.ordineVenditaCampoUtente, EntitaCampoUtente.ORDINE_VENDITA],
  [TracciatoFieldTypesEnum.anagraficaCampoUtente, EntitaCampoUtente.ANAGRAFICA],
  [TracciatoFieldTypesEnum.operatoreCampoUtente, EntitaCampoUtente.ACCOUNT_ORGANIZZAZIONE],
  // Feature 138: i campi utente del canale badge stanno sulla MEMBERSHIP, non sul badge, quindi
  // l'ambito e' lo stesso del canale Operatori.
  [TracciatoFieldTypesEnum.operatoreBadgeCampoUtente, EntitaCampoUtente.ACCOUNT_ORGANIZZAZIONE]
]);

@Component({
  selector: 'app-tracciati-field-create',
  templateUrl: './tracciati-field-create.component.html',
  styleUrls: ['./tracciati-field-create.component.scss'],
  standalone: false
})
export class TracciatiFieldCreateComponent implements OnInit {
  isNew = true;
  tracciatoId: number;
  tracciatoCode = '';
  tracciatoField: TracciatoField;
  fields: TracciatoField[] = [];
  loading = false;
  tracciatoFieldsEnum = TracciatoFieldsEnum;
  fieldTypeEnum = TracciatoFieldTypesEnum;
  campiUtenteDefs: CampoUtenteDef[] = [];
  loadingCampiUtente = false;

  constructor(
    private tracciatoService: TracciatiService,
    private modalCtrl: ModalController,
    private messageSave: SaveMessage,
    private campiUtenteService: CampiUtenteService,
    private organizzazioniService: OrganizzazioniService) { }

  get selectedCampoUtenteCodice(): string {
    const v = this.tracciatoField?.fieldName ?? '';
    return v.startsWith('CampoUtente.') ? v.substring('CampoUtente.'.length) : '';
  }

  /** Card "Campo di tipo chiave". */
  get showKeyTypeCard(): boolean {
    return KEY_TYPE_CARD_FIELD_TYPES.has(this.tracciatoField?.fieldType);
  }

  /** Card "Nome del campo" (catalogo statico; i tipi campo utente usano il picker dinamico). */
  get showFieldNameCard(): boolean {
    return FIELD_NAME_CARD_FIELD_TYPES.has(this.tracciatoField?.fieldType);
  }

  /** Card "Formato della stringa" / "Tipo campo". */
  get showFormatCard(): boolean {
    return FORMAT_CARD_FIELD_TYPES.has(this.tracciatoField?.fieldType)
      && this.tracciatoField?.fieldName !== TracciatoFieldsEnum.stato
      && !(this.tracciatoField?.fieldType === TracciatoFieldTypesEnum.quality
        && this.tracciatoField?.fieldName === TracciatoFieldsEnum.idCodNc);
  }

  /** Dentro la card formato: conversione di tipo invece della regex. */
  get usesTipoCampoFormat(): boolean {
    return TIPO_CAMPO_FORMAT_FIELD_TYPES.has(this.tracciatoField?.fieldType);
  }

  /** Dentro la card formato: editor delle regole regex invece dell'input libero. */
  get showFormatRules(): boolean {
    return !this.usesTipoCampoFormat
      && this.tracciatoField?.fieldType !== TracciatoFieldTypesEnum.fixed
      && this.tracciatoField?.fieldType !== TracciatoFieldTypesEnum.quality;
  }

  /**
   * Casella «salta la riga se il campo è vuoto» (FR-043): visibile sui soli tipi campo Operatori,
   * che sono gli unici in cui il flag viene onorato dal worker.
   */
  get showSkipRowIfEmpty(): boolean {
    return SKIP_ROW_IF_EMPTY_FIELD_TYPES.has(this.tracciatoField?.fieldType);
  }

  // Anteprima del valore prodotto dal campo selezionato (match per nome + tipo).
  get selectedFieldPreview(): string | undefined {
    return TRACCIATO_FIELDS.find(f =>
      f.value === this.tracciatoField?.fieldName &&
      f.fieldType === this.tracciatoField?.fieldType)?.preview;
  }

  ngOnInit() {
    if (this.tracciatoField == null) {
      // Copia: emptyField è un singleton condiviso a livello di modulo. Assegnarlo per riferimento
      // faceva sì che i valori del campo precedente (defaultValue, fieldFormat, ...) restassero
      // sull'oggetto e ricomparissero — anche se non visualizzati — alla creazione del campo successivo.
      this.tracciatoField = {...emptyField};
      // Propone il prossimo indice libero (max esistente + 1) per evitare SortIndex duplicati;
      // resta comunque modificabile dall'operatore.
      this.tracciatoField.sortIndex = this.nextSortIndex();
    } else {
      this.tracciatoField = {...this.tracciatoField};
    }
    this.tracciatoField.tracciatoId = this.tracciatoId;
    this.normalizeFieldName();
    // Pre-load Campi Utente quando il fieldType iniziale è dinamico (OdV o Anagrafica).
    if (this.isCampoUtenteType(this.tracciatoField.fieldType)) {
      this.loadCampiUtenteDefs();
    }
  }

  // True per i tipi campo "Campo Utente" dinamici (Ordine vendita, Anagrafica o Operatore).
  isCampoUtenteType(fieldType: TracciatoFieldTypesEnum): boolean {
    return CAMPO_UTENTE_ENTITA_BY_FIELD_TYPE.has(fieldType);
  }

  onFieldTypeChange() {
    this.normalizeFieldName();
    // Al cambio tipo verso un tipo Campo Utente, ricarica le definizioni dell'entità corretta
    // (reset esplicito: lo switch OdV↔Anagrafica deve riproporre il set giusto).
    if (this.isCampoUtenteType(this.tracciatoField.fieldType)) {
      this.campiUtenteDefs = [];
      this.loadCampiUtenteDefs();
    }
  }

  selectCampoUtente(e: SelectCustomEvent) {
    const codice = e.target.value as string;
    this.tracciatoField.fieldName = codice ? `CampoUtente.${codice}` : '';
  }

  cancel = () => void this.modalCtrl.dismiss(false, 'cancel');

  confirm() {
    this.normalizeFieldName();
    this.loading = true;
    if (this.isNew) {
      this.tracciatoService.createTracciatoFieldAsync(this.tracciatoField)
        .then(this.succesSave)
        .catch(this.errorSave)
        .finally(() => this.loading = false);
    } else {
      this.tracciatoService.updateTracciatoFieldAsync(this.tracciatoField)
        .then(this.succesSave)
        .catch(this.errorSave)
        .finally(() => this.loading = false);
    }
  }

  succesSave = (_field: TracciatoField) => {
    const code = (this.tracciatoCode ?? '').trim();
    this.messageSave.messageInfo(
      code ? `Tracciato ${code} aggiornato con successo.` : 'Operazione eseguita con successo.'
    );
    void this.modalCtrl.dismiss(_field, 'confirm');
  };

  errorSave = () => this.messageSave.messageError("Campo non valido.");

  private normalizeFieldName(): void {
    if (this.tracciatoField.fieldType === TracciatoFieldTypesEnum.external
      || this.tracciatoField.fieldType === TracciatoFieldTypesEnum.skip) {
      this.tracciatoField.fieldName = '';
    }
  }

  private nextSortIndex(): number {
    const maxIndex = (this.fields ?? []).reduce((max, f) => Math.max(max, f.sortIndex ?? 0), 0);
    return maxIndex + 1;
  }

  private loadCampiUtenteDefs() {
    // Entità derivata dal tipo campo selezionato (mappa esplicita, non un ternario annidato).
    const entita = CAMPO_UTENTE_ENTITA_BY_FIELD_TYPE.get(this.tracciatoField.fieldType)
      ?? EntitaCampoUtente.ORDINE_VENDITA;
    this.loadingCampiUtente = true;
    this.campiUtenteService.getDefinizioni(this.organizzazioniService.organizzazioneId, true, entita)
      .then(defs => this.campiUtenteDefs = defs ?? [])
      .catch(() => this.messageSave.messageError("Impossibile caricare i Campi Utente."))
      .finally(() => this.loadingCampiUtente = false);
  }
}
